import { useState, useEffect } from "react";
import { getApiUrl } from "../../../../api";

interface IconOption {
    value: string;
    label: string;
    icon_url: string | null;
    keywords: string;
}

interface CategoryOption {
    category_code: string;
    name: string;
    category_type: string;
    parent_category_code: string | null;
    icon_index: number | null;
    icon_url: string | null;
}

interface MenuRegisterProps {
    isOpen: boolean;
    onClose: () => void;
    kioskCode: string;
    defaultParentCode?: string;
}

interface FormData {
    menuName: string;
    menuType: string;
    icon: string;
    parentMenu: string;
    receptionType: string;
}

const MENU_TYPE_OPTIONS = [
    { value: "menu", label: "메뉴" },
    { value: "content", label: "컨텐츠" },
];

export default function MenuRegister({ isOpen, onClose, kioskCode, defaultParentCode = "" }: MenuRegisterProps) {
    const [formData, setFormData] = useState<FormData>({
        menuName: "",
        menuType: "",
        icon: "",
        parentMenu: defaultParentCode,
        receptionType: "",
    });
    const [iconOptions, setIconOptions] = useState<IconOption[]>([]);
    const [parentCategories, setParentCategories] = useState<CategoryOption[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [resultModal, setResultModal] = useState({ open: false, title: "", description: "", success: false });
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const getCmsToken = () => localStorage.getItem("cms_access_token");

    useEffect(() => {
        setFormData({ menuName: "", menuType: "", icon: "", parentMenu: defaultParentCode, receptionType: "" });
        setSearchTerm("");
        setOpenDropdown(null);
        if (!isOpen) return;
        
        const fetchIcons = async () => {
            try {
                const res = await fetch(getApiUrl("/api/categories/icons"), {
                    headers: { Authorization: `Bearer ${getCmsToken()}` }
                });
                if (res.ok) {
                    const icons = await res.json();
                    setIconOptions([
                        { value: "", label: "선택 안 함 (아이콘 없음)", icon_url: null, keywords: "none" },
                        ...icons.map((icon: { icon_index: number; icon_url: string; keywords: string }) => ({
                            value: icon.icon_index.toString(),
                            label: `아이콘 ${icon.icon_index}`,
                            icon_url: icon.icon_url,
                            keywords: icon.keywords,
                        }))
                    ]);
                }
            } catch (e) { console.error(e); }
        };
        const fetchParents = async () => {
            if (!kioskCode) return;
            try {
                const res = await fetch(getApiUrl(`/api/categories/kiosk/${kioskCode}`), {
                    headers: { Authorization: `Bearer ${getCmsToken()}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setParentCategories([
                        { category_code: "CA00000", name: "없음 (맨 첫 메인 메뉴에 표시)", category_type: "none", parent_category_code: null, icon_index: null, icon_url: null },
                        ...data.categories.map((cat: CategoryOption) => cat)
                    ]);
                }
            } catch (e) { console.error(e); }
        };
        fetchIcons();
        fetchParents();
    }, [isOpen, kioskCode]);

    const filteredCategories = parentCategories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSubmit = async () => {
        if (!formData.menuName.trim()) return setResultModal({ open: true, title: "메뉴명을 입력해주세요.", description: "", success: false });
        if (!formData.menuType) return setResultModal({ open: true, title: "메뉴 타입을 선택해주세요.", description: "", success: false });
        if (!formData.parentMenu) return setResultModal({ open: true, title: "상위 메뉴를 선택해주세요.", description: "", success: false });

        try {
            const res = await fetch(getApiUrl("/api/categories"), {
                method: "POST",
                headers: { Authorization: `Bearer ${getCmsToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: formData.menuName,
                    category_type: formData.menuType,
                    icon_index: formData.icon || null,
                    parent_category_code: formData.parentMenu,
                    kiosk_code: kioskCode,
                    is_visible: 1,
                })
            });
            if (res.ok) {
                setResultModal({ open: true, title: "메뉴가 정상적으로 등록되었습니다.", description: "등록된 메뉴는 목록에서 바로 확인하실 수 있습니다.", success: true });
            } else {
                const err = await res.json();
                setResultModal({ open: true, title: "등록에 실패했습니다.", description: err.detail || "", success: false });
            }
        } catch {
            setResultModal({ open: true, title: "등록 중 오류가 발생했습니다.", description: "", success: false });
        }
    };

    if (!isOpen) return null;

    const selectedMenuType = MENU_TYPE_OPTIONS.find(o => o.value === formData.menuType);
    const selectedIcon = iconOptions.find(o => o.value === formData.icon);
    const selectedParent = parentCategories.find(c => c.category_code === formData.parentMenu);

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] mx-4 flex flex-col">
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="text-[20px] font-bold text-[#2a1f1a]">신규 메뉴 등록하기</h2>
                    </div>
                    <div className="p-6 space-y-4 flex-1">

                        {/* 메뉴명 */}
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">메뉴명 <span className="text-red-400">*</span></label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267]"
                                value={formData.menuName}
                                onChange={(e) => setFormData(prev => ({ ...prev, menuName: e.target.value }))}
                                placeholder="메뉴명을 입력하세요"
                            />
                        </div>

                        {/* 메뉴 타입 */}
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">메뉴 타입 <span className="text-red-400">*</span></label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg text-left bg-white flex justify-between items-center"
                                    onClick={() => setOpenDropdown(openDropdown === "menuType" ? null : "menuType")}
                                >
                                    <span className={selectedMenuType ? "text-[#2a1f1a]" : "text-gray-300"}>{selectedMenuType?.label || "선택하세요"}</span>
                                    <span className="text-gray-400">▾</span>
                                </button>
                                {openDropdown === "menuType" && (
                                    <div className="absolute top-full left-0 right-0 bg-white border border-[#e8e0dc] rounded-lg shadow-lg z-10 mt-1 max-h-[200px] overflow-y-auto">
                                        {MENU_TYPE_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className="w-full px-3 py-2 text-[13px] text-left hover:bg-[#f5f2f0] text-[#2a1f1a]"
                                                onClick={() => { setFormData(prev => ({ ...prev, menuType: opt.value })); setOpenDropdown(null); }}
                                            >{opt.label}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 상위 메뉴 */}
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">상위 메뉴 <span className="text-red-400">*</span></label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg text-left bg-white flex justify-between items-center"
                                    onClick={() => setOpenDropdown(openDropdown === "parentMenu" ? null : "parentMenu")}
                                >
                                    <span className={selectedParent ? "text-[#2a1f1a]" : "text-gray-300"}>{selectedParent?.name || "선택하세요"}</span>
                                    <span className="text-gray-400">▾</span>
                                </button>
                                {openDropdown === "parentMenu" && (
                                    <div className="absolute top-full left-0 right-0 bg-white border border-[#e8e0dc] rounded-lg shadow-lg z-10 mt-1max-h-[200px] overflow-y-auto">
                                        <div className="p-2 border-b border-[#f5f2f0]">
                                            <input
                                                type="text"
                                                className="w-full px-2 py-1 text-[12px] border border-[#e8e0dc] rounded outline-none focus:border-[#887267]"
                                                placeholder="검색..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <div className="max-h-[160px] overflow-y-auto">
                                            {filteredCategories.map(cat => (
                                                <button
                                                    key={cat.category_code}
                                                    type="button"
                                                    className="w-full px-3 py-2 text-[13px] text-left hover:bg-[#f5f2f0] text-[#2a1f1a]"
                                                    onClick={() => { setFormData(prev => ({ ...prev, parentMenu: cat.category_code })); setOpenDropdown(null); setSearchTerm(""); }}
                                                >{cat.name}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 아이콘 */}
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">아이콘</label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg text-left bg-white flex justify-between items-center"
                                    onClick={() => setOpenDropdown(openDropdown === "icon" ? null : "icon")}
                                >
                                    <span className="flex items-center gap-2 text-[#2a1f1a]">
                                        {selectedIcon?.icon_url && (
                                            <img src={selectedIcon.icon_url} className="w-5 h-5 object-contain" style={{ filter: "brightness(0)" }} alt="" />
                                        )}
                                        {selectedIcon?.label || "선택 안 함"}
                                    </span>
                                    <span className="text-gray-400">▾</span>
                                </button>
                                {openDropdown === "icon" && (
                                    <div className="absolute top-full left-0 right-0 bg-white border border-[#e8e0dc] rounded-lg shadow-lg z-[100] mt-1 max-h-[200px] overflow-y-auto">
                                        {iconOptions.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className="w-full px-3 py-2 text-[13px] text-left hover:bg-[#f5f2f0] text-[#2a1f1a] flex items-center gap-2"
                                                onClick={() => { setFormData(prev => ({ ...prev, icon: opt.value })); setOpenDropdown(null); }}
                                            >
                                                {opt.icon_url && <img src={opt.icon_url} className="w-5 h-5 object-contain" style={{ filter: "brightness(0)" }} alt="" />}
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 버튼 */}
                    <div className="p-6 pt-0 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-[14px]"
                        >취소</button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] transition-colors text-[14px] font-semibold"
                        >등록</button>
                    </div>
                </div>
            </div>

            {/* 결과 모달 */}
            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-1">{resultModal.title}</p>
                        {resultModal.description && <p className="text-[12px] text-gray-400 mb-4">{resultModal.description}</p>}
                        <button
                            onClick={() => {
                                setResultModal(prev => ({ ...prev, open: false }));
                                if (resultModal.success) onClose();
                            }}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold mt-2"
                        >확인</button>
                    </div>
                </div>
            )}
        </>
    );
}